import { useCallback, useEffect, useRef, useState, type ReactElement } from "react";
import { ListTodo, MessageSquare, Pin, Plus, StickyNote } from "lucide-react";
import { color, radius, space, typography } from "@tokens/index";
import {
  Button,
  IconButton,
  SegmentedControl,
  Text,
  returnFocus,
  type SegmentedControlOption,
} from "../../primitives";
import { pluralize, SNOOZE_QUICK_OPTIONS, toLocalDateTimeInput } from "./formatters";
import { hairline } from "./expandMotion";
import { Collapse } from "./Collapse";
import { ComposerField } from "./ComposerField";
import { InsertedRow } from "./InsertedRow";
import { useNewRowDetector } from "./useNewRowDetector";
import { RailRow } from "./RailRow";
import { TaskRailEntry } from "./TaskRailEntry";
import type {
  AddTaskDraft,
  DestinationQueue,
  RailEntry,
  RailGroup,
  RailSelection,
  TaskTypeOption,
} from "./types";

const nativeControl = {
  appearance: "auto",
  width: "100%",
  height: 36,
  fontFamily: typography.family.system,
  fontSize: typography.scale.sm,
  color: color.fg.default,
  background: color.bg.surface,
  border: hairline,
  borderRadius: radius.md,
  padding: `0 ${space[2]}`,
} as const;

type CreatorKind = "note" | "task";

const KIND_OPTIONS: readonly SegmentedControlOption<CreatorKind>[] = [
  { value: "note", label: "Note", icon: StickyNote },
  { value: "task", label: "Task", icon: ListTodo },
];

const FOLLOW_UP_TASK_TYPE = "follow-up";
const ROUTE_AUTOMATICALLY = "";

function defaultTaskType(options: readonly TaskTypeOption[]): string {
  return options.find((option) => option.id === FOLLOW_UP_TASK_TYPE)?.id ?? options[0]?.id ?? "";
}

function defaultDestination(queues: readonly DestinationQueue[], requireExplicit: boolean): string {
  if (!requireExplicit) return ROUTE_AUTOMATICALLY;
  return queues.find((queue) => queue.isPersonal)?.id ?? queues[0]?.id ?? ROUTE_AUTOMATICALLY;
}

/**
 * Inline creator at the top of the rail. Tab order: body, kind, type,
 * destination, due, Add. Enter adds, Shift+Enter breaks a line, Escape
 * cancels. Closing (either way) hands focus back to the `+` control.
 */
function RailCreator({
  taskTypeOptions,
  destinationQueues,
  requireExplicitDestination,
  initialKind = "note",
  onAddTask,
  onAddNote,
  onClose,
}: {
  readonly taskTypeOptions: readonly TaskTypeOption[];
  readonly destinationQueues: readonly DestinationQueue[];
  readonly requireExplicitDestination: boolean;
  readonly initialKind?: CreatorKind;
  readonly onAddTask: (draft: AddTaskDraft) => void;
  readonly onAddNote: (body: string) => void;
  readonly onClose: () => void;
}): ReactElement {
  const [kind, setKind] = useState<CreatorKind>(initialKind);
  const [body, setBody] = useState("");
  const [taskType, setTaskType] = useState(() => defaultTaskType(taskTypeOptions));
  const [destination, setDestination] = useState(() =>
    defaultDestination(destinationQueues, requireExplicitDestination),
  );
  const [dueDate, setDueDate] = useState("");

  useEffect(() => {
    if (taskType.length === 0 && taskTypeOptions.length > 0) {
      setTaskType(defaultTaskType(taskTypeOptions));
    }
  }, [taskType, taskTypeOptions]);

  useEffect(() => {
    if (requireExplicitDestination && destination.length === 0 && destinationQueues.length > 0) {
      setDestination(defaultDestination(destinationQueues, true));
    }
  }, [requireExplicitDestination, destination, destinationQueues]);

  const showRouteAutomatically = !requireExplicitDestination;
  const destinationRequired = requireExplicitDestination;
  const canSubmit =
    kind === "note"
      ? body.trim().length > 0
      : taskType.length > 0 && (!requireExplicitDestination || destination.length > 0);

  const applyDueInMinutes = useCallback((minutes: number) => {
    setDueDate(toLocalDateTimeInput(new Date(Date.now() + minutes * 60_000)));
  }, []);

  const handleSubmit = useCallback(() => {
    const trimmed = body.trim();
    if (kind === "note") {
      if (trimmed.length === 0) return;
      onAddNote(trimmed);
    } else {
      if (taskType.length === 0) return;
      if (requireExplicitDestination && destination.length === 0) return;
      onAddTask({
        taskType,
        body: trimmed,
        ...(dueDate.length > 0 ? { dueDate: new Date(dueDate).toISOString() } : {}),
        ...(destination.length > 0 ? { targetQueueId: destination } : {}),
      });
    }
    onClose();
  }, [
    body,
    kind,
    taskType,
    dueDate,
    destination,
    requireExplicitDestination,
    onAddNote,
    onAddTask,
    onClose,
  ]);

  return (
    <div
      data-testid="rail-creator"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: space[2],
        padding: `${space[2]} ${space[2]} ${space[3]}`,
        margin: `0 ${space[2]}`,
        borderRadius: radius.lg,
        background: color.bg.surface2,
      }}
    >
      <ComposerField
        autoFocus
        value={body}
        onChange={setBody}
        onSubmit={handleSubmit}
        onCancel={onClose}
        placeholder={kind === "note" ? "Capture a note…" : "Describe the task (optional)…"}
        ariaLabel={kind === "note" ? "Note body" : "Task body"}
        testId="rail-creator-body"
      />

      <SegmentedControl<CreatorKind>
        options={KIND_OPTIONS}
        value={kind}
        onChange={setKind}
        size="sm"
        fullWidth
        ariaLabel="Item kind"
      />

      {kind === "task" && (
        <>
          <select
            value={taskType}
            onChange={(e) => setTaskType(e.target.value)}
            aria-label="Task type"
            style={nativeControl}
          >
            {taskTypeOptions.map((option) => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </select>
          {(showRouteAutomatically || destinationQueues.length > 0) && (
            <label style={{ display: "flex", flexDirection: "column", gap: space[1], minWidth: 0 }}>
              <Text role="caps" size="xs" tone="muted">
                Deliver to
              </Text>
              <select
                value={destination}
                onChange={(e) => setDestination(e.target.value)}
                aria-label="Deliver to"
                required={destinationRequired}
                style={nativeControl}
              >
                {showRouteAutomatically ? (
                  <option value={ROUTE_AUTOMATICALLY}>Route automatically</option>
                ) : null}
                {destinationQueues.map((queue) => (
                  <option key={queue.id} value={queue.id}>
                    {queue.name}
                  </option>
                ))}
              </select>
            </label>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: space[1], minWidth: 0 }}>
            <Text role="caps" size="xs" tone="muted">
              Due
            </Text>
            <div style={{ display: "flex", flexWrap: "wrap", gap: space[1] }}>
              {SNOOZE_QUICK_OPTIONS.map((opt) => (
                <Button
                  key={opt.label}
                  type="button"
                  variant="soft"
                  size="sm"
                  aria-label={`Due in ${opt.label}`}
                  onClick={() => applyDueInMinutes(opt.minutes)}
                >
                  {opt.label}
                </Button>
              ))}
            </div>
            <input
              type="datetime-local"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              aria-label="Due date and time"
              style={{ ...nativeControl, fontSize: typography.scale.xs }}
            />
          </div>
        </>
      )}

      <div style={{ display: "flex", gap: space[2], justifyContent: "flex-end" }}>
        <Button variant="primary" size="sm" disabled={!canSubmit} onClick={handleSubmit}>
          <Plus size={14} strokeWidth={2.5} aria-hidden="true" />
          Add
        </Button>
        <Button variant="ghost" size="sm" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

export interface BoardRailProps {
  readonly groups: readonly RailGroup[];
  readonly selectedItemId: RailSelection;
  readonly onSelectItem: (id: RailSelection) => void;
  readonly generalMessageCount: number;
  readonly rootNoteCount: number;
  readonly now: Date;
  readonly taskTypeOptions: readonly TaskTypeOption[];
  readonly destinationQueues?: readonly DestinationQueue[];
  readonly requireExplicitDestination?: boolean;
  readonly initialCreatorOpen?: boolean;
  readonly onCreatorClosed?: () => void;
  readonly onAddTask: (draft: AddTaskDraft) => void;
  readonly onAddNote: (body: string) => void;
  /** Pixel width on `medium` and up. `undefined` fills the parent (compact). */
  readonly width: number | undefined;
  /**
   * `true` while the workflow is still loading and `groups` hold placeholder
   * entries. Rows that arrive with the data then do not replay an insertion.
   */
  readonly loading?: boolean;
}

export function BoardRail({
  groups,
  selectedItemId,
  onSelectItem,
  generalMessageCount,
  rootNoteCount,
  now,
  taskTypeOptions,
  destinationQueues = [],
  requireExplicitDestination = false,
  initialCreatorOpen = false,
  onCreatorClosed,
  onAddTask,
  onAddNote,
  width,
  loading = false,
}: BoardRailProps): ReactElement {
  const [creatorOpen, setCreatorOpen] = useState(initialCreatorOpen);
  const plusRef = useRef<HTMLButtonElement>(null);

  const allKeys = groups.flatMap((g) => g.entries.map((e) => e.key));
  const isNewRow = useNewRowDetector(allKeys, !loading);

  const closeCreator = useCallback(() => {
    setCreatorOpen(false);
    onCreatorClosed?.();
    if (plusRef.current) returnFocus(plusRef.current);
  }, [onCreatorClosed]);

  const renderEntry = (entry: RailEntry): ReactElement | null => {
    if (entry.kind === "general") {
      return (
        <RailRow
          label="Chat"
          sublabel={
            generalMessageCount > 0
              ? pluralize(generalMessageCount, "message")
              : "Workflow conversation"
          }
          icon={<MessageSquare size={15} strokeWidth={2.25} aria-hidden="true" />}
          selected={selectedItemId === null}
          onSelect={() => onSelectItem(null)}
          accent={color.accent.primary}
          trailing={undefined}
        />
      );
    }
    if (entry.kind === "notes") {
      return (
        <RailRow
          label="All notes"
          sublabel={rootNoteCount > 0 ? pluralize(rootNoteCount, "note") : "Workflow notes"}
          icon={<StickyNote size={15} strokeWidth={2.25} aria-hidden="true" />}
          selected={selectedItemId === null}
          onSelect={() => onSelectItem(null)}
          accent={color.note.accent}
          trailing={undefined}
        />
      );
    }
    const item = entry.item;
    if (item === null) return null;
    if (entry.kind === "task") {
      return (
        <TaskRailEntry
          item={item}
          selected={selectedItemId === item.id}
          now={now}
          onSelectItem={onSelectItem}
        />
      );
    }
    return (
      <RailRow
        label={item.pinned ? "Pinned note" : "Note"}
        sublabel={item.body}
        icon={
          item.pinned ? (
            <Pin size={15} strokeWidth={2.25} aria-hidden="true" />
          ) : (
            <StickyNote size={15} strokeWidth={2.25} aria-hidden="true" />
          )
        }
        selected={selectedItemId === item.id}
        onSelect={() => onSelectItem(item.id)}
        accent={color.note.accent}
        trailing={undefined}
      />
    );
  };

  return (
    <div
      data-testid="workflow-board-rail"
      style={{
        width: width ?? "100%",
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        // One tonal step above the panel's canvas: the rail reads as a region
        // without a border.
        background: color.bg.surface,
        minHeight: 0,
        minWidth: 0,
        // Rows query this width to drop the body preview on a narrow rail.
        containerType: "inline-size",
        containerName: "rail",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          height: 40,
          flexShrink: 0,
          padding: `0 ${space[2]} 0 ${space[4]}`,
        }}
      >
        <Text role="caps" size="xs" tone="muted">
          Work items
        </Text>
        <IconButton
          ref={plusRef}
          icon={Plus}
          size="sm"
          variant="ghost"
          aria-label="Add task or note"
          aria-expanded={creatorOpen}
          onClick={() => (creatorOpen ? closeCreator() : setCreatorOpen(true))}
        />
      </div>

      <div
        style={{
          // `listStack` minus its gap: `InsertedRow` owns the space below each
          // row so the gap grows in with the row. Rows never shrink (see
          // RailRow), so the stack scrolls instead of collapsing them.
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          display: "flex",
          flexDirection: "column",
          padding: `0 0 ${space[3]}`,
        }}
      >
        <Collapse open={creatorOpen} testId="rail-creator-collapse">
          <div style={{ paddingBottom: space[2] }}>
            <RailCreator
              taskTypeOptions={taskTypeOptions}
              destinationQueues={destinationQueues}
              requireExplicitDestination={requireExplicitDestination}
              initialKind={initialCreatorOpen ? "task" : "note"}
              onAddTask={onAddTask}
              onAddNote={onAddNote}
              onClose={closeCreator}
            />
          </div>
        </Collapse>

        {groups.map((group) =>
          group.entries.length === 0 ? null : (
            <section
              key={group.key}
              aria-label={group.label}
              style={{ display: "flex", flexDirection: "column", minWidth: 0 }}
            >
              <Text
                role="caps"
                size="xs"
                tone="subtle"
                style={{ display: "block", padding: `${space[2]} ${space[4]} ${space[1]}` }}
              >
                {group.label}
              </Text>
              {group.entries.map((entry) => (
                <InsertedRow
                  key={entry.key}
                  isNew={isNewRow(entry.key)}
                  gap={2}
                  style={{ padding: `0 ${space[2]}` }}
                >
                  {renderEntry(entry)}
                </InsertedRow>
              ))}
            </section>
          ),
        )}
      </div>
    </div>
  );
}
